import { Illustration, Typography } from "@saviynt/design-system";
import { Component, ReactNode } from "react";
import { FormattedMessage } from "react-intl";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundaryInner extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      const isDev = import.meta.env.DEV;

      return (
        <div className="flex flex-col items-center justify-center min-h-screen p-8">
          <Illustration kind="Error" />
          <Typography kind="h2" className="mt-4">
            <FormattedMessage
              id="q9bRRe"
              defaultMessage="Something went wrong"
              description="Error boundary message when app crashes"
            />
          </Typography>
          {isDev && this.state.error && (
            <div className="mt-4 p-4 bg-gray-100 rounded-lg max-w-3xl overflow-auto">
              <Typography
                kind="body2"
                className="font-mono whitespace-pre-wrap"
              >
                {this.state.error.toString()}
                {this.state.error.stack}
              </Typography>
            </div>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundaryInner;
